import * as yup from 'yup';
import { ChangeEvent, useRef, useState } from 'react';
import { DocumentNode } from 'graphql';
import { FormManager, useForm } from 'react-form-state-manager';
import { Trash2, X } from 'react-feather';
import { addDays } from 'date-fns';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';
import times from 'lodash/times';

import {
  AddOrderMutation as AddOrder,
  CouponType,
  GetCheckoutSummaryQuery as GetCheckoutSummary,
  GetOrderDataQuery as GetOrderData,
  InvoiceDetailsInput,
  RegistrationsInput,
  TeamInput,
} from '__generated__/graphql';

import { ErrorBag, filterPrefix, getServerErrors } from '../../../../common/helpers';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import AddOrderMutation from './AddOrderMutation';
import ContactPicker, { Participant as SelectedParticipant } from '../../Contacts/ContactPicker';
import EventPicker, { Event as SelectedEvent } from '../../../Events/EventPicker';
import FindOrAddTeamForm, { findOrAddTeamSchema } from '../../Team/FindOrAddTeamForm';
import GetOrderDataQuery from './GetOrderDataQuery';
import GetParticipantOrders from './GetParticipantOrdersQuery';
import InvoiceDetailsForm, {
  emptyInvoiceDetails, getInvoiceSchema,
} from '../../../../common/Invoice/InvoiceDetailsForm';
import PaymentReminderInfo from '../../../Common/PaymentReminderInfo';
import UI from '../../../../common/UI';
import useCheckoutSummary from '../../../../common/useCheckoutSummary';
import useCurrencyInput from '../../../../common/useCurrencyInput';
import useIntegerInput from '../../../../common/useIntegerInput';
import useLocale from '../../../../common/useLocale';
import useMutation from '../../../../api/useMutation';
import useProject from '../../../useProject';
import useQuery from '../../../../api/useQuery';
import useTimeSlotFormatter from '../../../../common/Tickets/useTimeSlotFormatter';
import useValidation from '../../../../common/useValidation';

type Event = GetOrderData['event'];
type Coupon = Event['coupons'][0];
type Invitation = Event['invitations'][0];
type Ticket = Event['tickets'][0];
type Product = Event['products'][0];
type Participant = GetOrderData['participant'];
type Registration = Participant['personal_registrations'][0];
type AddedOrder = AddOrder['addOrder'];
type CheckoutSummary = GetCheckoutSummary['checkoutSummary'];

// Maximum number of registrations per order.
const MAX_REGISTRATIONS = 100;

interface Promotion {
  id: string;
  amount: number;
}

interface TimeSlot {
  id: string;
}

interface ProductVariant {
  id: string;
}

interface Sellable {
  id: string;
  promotions: Promotion[];
  time_slots?: TimeSlot[];
  product_variants?: ProductVariant[];
}

function getKeyedSellables<T extends Sellable>(sellables: T[]) {
  const result: { [promotionId: string]: T } = {};

  sellables.forEach((sellable) => {
    sellable.promotions.forEach((promotion) => {
      result[promotion.id] = sellable;
    });
  });

  return result;
}

const preprocessRegistrations = (
  registrations: RegistrationData[], standAloneUpgrades: UpgradeData[], event: Event, participant: Participant,
): RegistrationsInput => {
  const keyedTickets = getKeyedSellables(event.tickets);
  const createRegistrations = registrations.filter(({ ticket }) => !!ticket);

  return {
    create: createRegistrations
      .map((registration) => {
        // Create arrays of `ticket.units` registrations
        const ticket = keyedTickets[registration.ticket.promotion.id];

        return times(ticket.units * registration.quantity, (index: number) => ({
          ticket: {
            id: ticket.id,
          },
          time_slot: registration.ticket.time_slot ? {
            id: registration.ticket.time_slot.id,
          } : null,
          purchase: {
            promotion: {
              id: registration.ticket.promotion.id,
            },
            amount: index % ticket.units === 0 ? registration.ticket.amount : 0,
          },
          upgrades: preprocessProducts(registration.upgrades, event),
          // When adding a single ticket, it is assigned to the buyer, otherwise all tickets are unassigned
          participant:
            createRegistrations.length === 1 && createRegistrations[0].quantity === 1 && ticket.units === 1 ? {
              id: participant.id,
            } : null,
        }));
      })
      .reduce((flattened, registrations) => [
        // Flatten the 2D-array
        ...flattened, ...registrations,
      ], []),
    update: registrations
      .filter(({ registration, upgrades }) => (
        !!registration && upgrades.filter((upgrade) => !!upgrade.promotion).length > 0
      ))
      .map(({ registration, upgrades }) => ({
        id: registration.id,
        upgrades: preprocessProducts(upgrades, event),
      })),
    stand_alone_upgrades: preprocessProducts(standAloneUpgrades, event),
  };
};

const preprocessProducts = (upgrades: UpgradeData[], event: Event) => {
  const keyedProducts = getKeyedSellables(event.products);

  return upgrades
    .filter((upgrade) => !!upgrade.promotion)
    .reduce((upgrades, { quantity, ...upgrade }) => [
      ...upgrades,
      ...times(quantity, () => ({
        product: {
          id: keyedProducts[upgrade.promotion.id].id,
        },
        product_variant: upgrade.product_variant ? {
          id: upgrade.product_variant.id,
        } : null,
        purchase: {
          promotion: {
            id: upgrade.promotion.id,
          },
          amount: upgrade.amount,
        },
      })),
    ], []);
};

interface TicketData {
  promotion: { id: string; };
  time_slot?: { id: string; };
  amount: number;
}

interface UpgradeData {
  promotion?: { id: string; };
  product_variant?: { id: string; };
  amount?: number;
  quantity: number;
}

interface RegistrationData {
  registration?: { id: string; };
  ticket?: TicketData;
  upgrades: UpgradeData[];
  quantity: number;
}

interface AddOrderData {
  event: { id: string; };
  registrations: RegistrationData[];
  stand_alone_upgrades: UpgradeData[];
  coupon: string | null;
  invitation_code: string | null;
  invoice: InvoiceDetailsInput;
  due_date: string;
  team: TeamInput | null;
}

const addOrderSchema = yup.object({
  registrations: yup.array().required().of(yup.object({
    ticket: yup.object().nullable().default(null).shape({
      amount: yup.number().required(),
    }),
    upgrades: yup.array().of(yup.object({
      amount: yup.number().nullable().when(['promotion'], {
        is: (promotion: any) => !!promotion,
        then: yup.number().required(),
      }),
    })),
  })).when(['stand_alone_upgrades'], {
    is: (value: any[]) => (value || []).length === 0,
    then: yup.array().min(1),
  }),
  stand_alone_upgrades: yup.array().of(yup.object({
    amount: yup.number().nullable().when(['promotion'], {
      is: (promotion: any) => !!promotion,
      then: yup.number().required(),
    }),
  })),
  team: findOrAddTeamSchema.nullable(),
  invoice: getInvoiceSchema().nullable(),
});

export interface AddOrderFormProps {
  participantId?: string;
  eventId?: string;
  refetchQueries?: DocumentNode[];
  onContinue?: () => void;
  onSuccess?: (order: AddedOrder) => void;
}

export const AddOrderForm = ({
  participantId: initialParticipantId,
  eventId: initialEventId,
  onContinue,
  onSuccess,
  refetchQueries = [GetParticipantOrders],
}: AddOrderFormProps) => {
  const { t } = useTranslation();

  const [participantId, setParticipantId] = useState(initialParticipantId);
  const [selectedParticipant, setSelectedParticipant] = useState<SelectedParticipant>();

  const selectParticipant = (participant?: SelectedParticipant) => {
    setParticipantId(participant?.id);
    setSelectedParticipant(participant);
  };

  const [eventId, setEventId] = useState(initialEventId);

  const selectEvent = (event: SelectedEvent) => {
    setEventId(event?.id);
  };

  const [step, setStep] = useState(1);

  const nextStep = () => {
    setStep(2);
    onContinue?.();
  };

  const { data: orderData } = useQuery(
    GetOrderDataQuery,
    {
      variables: {
        event_id: eventId,
        participant_id: participantId,
      },
      skip: !eventId || !participantId,
    },
  );

  const event = orderData?.event;
  const participant = orderData?.participant;

  if (step === 1) {
    return (
      <UI.FormGrid>
        <UI.Div>
          <UI.H4 sc={{ mb: 2 }}>
            {t('add_order')}
          </UI.H4>
          <UI.Paragraph sc={{ mb: 0 }}>
            {t('add_order_description')}
          </UI.Paragraph>
        </UI.Div>
        <UI.HR />
        <UI.InputGroup style={{ position: 'relative', zIndex: 2 }}>
          <UI.InputLabel>
            {t('event')}
          </UI.InputLabel>
          <EventPicker
            eventId={eventId}
            onChange={selectEvent}
            nullable={false}
            preselect={(events) => (events.length === 1 ? events[0] : undefined)}
          />
        </UI.InputGroup>
        {!initialParticipantId && (
          <UI.InputGroup style={{ position: 'relative', zIndex: 1 }}>
            <UI.InputLabel>
              {t('contact')}
            </UI.InputLabel>
            <ContactPicker
              participant={selectedParticipant}
              onChange={selectParticipant}
              nullable={false}
            />
          </UI.InputGroup>
        )}
        <UI.HR />
        <UI.Div sc={{ textAlign: 'right' }}>
          <UI.Button
            onClick={() => nextStep()}
            disabled={!event || !participant}
            sc={{ brand: 'secondary' }}
          >
            {t('next')}
          </UI.Button>
        </UI.Div>
      </UI.FormGrid>
    );
  }

  return (
    <BuildOrderForm
      event={event}
      participant={participant}
      onSuccess={onSuccess}
      refetchQueries={refetchQueries}
    />
  );
};

const emptyUpgrade: UpgradeData = { quantity: 1 };
const emptyRegistration: RegistrationData = { upgrades: [], quantity: 1 };

interface BuildOrderFormProps {
  event: Event;
  participant: Participant;
  refetchQueries: DocumentNode[];
  onSuccess?: (order: AddedOrder) => void;
}

const BuildOrderForm = ({ event, participant, onSuccess, refetchQueries }: BuildOrderFormProps) => {
  const { t } = useTranslation();
  const { formatCurrency, formatInteger, formatDate } = useLocale();
  const project = useProject();
  const notifier = useNotifier();

  const personalRegistrations = participant.personal_registrations;
  const nonpersonalRegistrations = participant.nonpersonal_registrations;

  const form = useForm<AddOrderData>({
    values: {
      event: {
        id: event.id,
      },
      registrations: [emptyRegistration],
      stand_alone_upgrades: [emptyUpgrade],
      coupon: null,
      invitation_code: null,
      due_date: formatDate(addDays(new Date(), 28), { format: 'internal_date' }),
      invoice: Object.keys(emptyInvoiceDetails).reduce(
        (invoiceDetails, key: keyof typeof emptyInvoiceDetails) => ({
          ...invoiceDetails,
          [key]: participant.invoice_details[key] !== null
            ? participant.invoice_details[key]
            : invoiceDetails[key],
        }),
        {
          ...emptyInvoiceDetails,
          country: project.organisation_country,
        },
      ),
      team: null,
    },
  });

  const keyedTickets = getKeyedSellables(event.tickets);
  const hasBusinessTickets = form.values.registrations.filter(
    (registration) => keyedTickets[registration.ticket?.promotion.id]?.business,
  ).length > 0;
  const hasNonBusinessTickets = form.values.registrations.filter(
    (registration) => keyedTickets[registration.ticket?.promotion.id]?.business === false,
  ).length > 0;
  const hasTickets = hasBusinessTickets || hasNonBusinessTickets;

  const [specifyInvoiceDetails, setSpecifyInvoiceDetails] = useState(false);
  const requireInvoiceDetails = hasBusinessTickets || specifyInvoiceDetails;
  const invoiceDetails = requireInvoiceDetails ? form.values.invoice : null;

  const registrationsInput = preprocessRegistrations(
    form.values.registrations,
    form.values.stand_alone_upgrades,
    event,
    participant,
  );

  const [addOrder, { loading, error }] = useMutation(
    AddOrderMutation,
    {
      variables: {
        input: {
          event: { id: form.values.event.id },
          participant: { id: participant.id },
          registrations: registrationsInput,
          coupon: form.values.coupon,
          invitation_code: hasTickets ? form.values.invitation_code : null,
          team: hasNonBusinessTickets && form.values.team ? {
            id: form.values.team.id,
            title: form.values.team.title,
          } : null,
          invoice: invoiceDetails,
          due_date: form.values.due_date,
        },
      },
      onCompleted: (data) => {
        onSuccess?.(data.addOrder);
        notifier.success(t('notifications.order_added'));
      },
      refetchQueries,
      awaitRefetchQueries: true,
    },
  );

  const { valid, errors } = useValidation({
    schema: addOrderSchema,
    values: {
      ...form.values,
      registrations: form.values.registrations.slice(0, form.values.registrations.length - 1),
      team: hasNonBusinessTickets ? form.values.team : null,
      invoice: invoiceDetails,
    },
    externalErrors: getServerErrors(error),
  });

  const { checkoutSummary, loading: loadingTotals } = useCheckoutSummary({
    event: form.values.event,
    cart: null,
    registrations: registrationsInput,
    coupon: form.values.coupon,
    couponValidation: false,
    vatId: specifyInvoiceDetails ? form.values.invoice.vat_id : null,
  });

  // Generate unique key props for the list of registration forms
  const registrationKeys = useRef(form.values.registrations.map((value, index) => index));

  const addRegistration = () => {
    form.set('registrations', (registrations: RegistrationData[]) => [
      ...registrations,
      emptyRegistration,
    ]);
    registrationKeys.current = [...registrationKeys.current, Math.max(0, ...registrationKeys.current) + 1];
  };

  const deleteRegistration = (index: number) => {
    form.delete('registrations', index);
    registrationKeys.current.splice(index, 1);
  };

  const standAloneProducts = event.products.filter((product) => product.stand_alone) || [];

  // Generate unique key props for the list of upgrade forms
  const standAloneUpgradeKeys = useRef(form.values.stand_alone_upgrades.map((value, index) => index));

  const setStandAloneUpgrade = (upgradeData: UpgradeData, upgradeIndex: number) => {
    form.set(`stand_alone_upgrades.${upgradeIndex}`, upgradeData);

    if (upgradeIndex === form.values.stand_alone_upgrades.length - 1) {
      addStandAloneUpgrade();
    }
  };

  const addStandAloneUpgrade = () => {
    form.set('stand_alone_upgrades', (upgrades: UpgradeData[]) => ([
      ...upgrades, emptyUpgrade,
    ]));
    standAloneUpgradeKeys.current = [
      ...standAloneUpgradeKeys.current,
      Math.max(0, ...standAloneUpgradeKeys.current) + 1,
    ];
  };

  const deleteStandAloneUpgrade = (index: number) => {
    form.delete('stand_alone_upgrades', index);
    standAloneUpgradeKeys.current.splice(index, 1);
  };

  const couponRef = useRef<HTMLInputElement>();

  const [
    selectedCoupon, setSelectedCoupon,
  ] = useState<Coupon>();

  const handleCouponChange = (changeEvent: ChangeEvent<HTMLSelectElement>) => {
    const newSelectedCoupon = event.coupons[parseInt(changeEvent.target.value, 10)];
    setSelectedCoupon(newSelectedCoupon);
    form.set('coupon', newSelectedCoupon?.prefix);

    // Focus the coupon code input after the next render
    window.setTimeout(() => {
      if (couponRef.current) {
        couponRef.current.focus();
      }
    }, 0);
  };

  const clearCoupon = () => {
    form.set('coupon', null);
    setSelectedCoupon(null);
  };

  const invitationCodeRef = useRef<HTMLInputElement>();

  const [
    selectedInvitation, setSelectedInvitation,
  ] = useState<Invitation>();

  const handleInvitationChange = (changeEvent: ChangeEvent<HTMLSelectElement>) => {
    const newSelectedInvitation = event.invitations[parseInt(changeEvent.target.value, 10)];
    setSelectedInvitation(newSelectedInvitation);
    form.set('invitation_code', newSelectedInvitation?.prefix);

    // Focus the invitation code input after the next render
    window.setTimeout(() => {
      if (invitationCodeRef.current) {
        invitationCodeRef.current.focus();
      }
    }, 0);
  };

  const clearInvitation = () => {
    form.set('invitation_code', null);
    setSelectedInvitation(null);
  };

  return (
    <UI.Form onSubmit={() => addOrder()}>
      <UI.H4 sc={{ mb: 2 }}>
        {`${t('add_order')}: ${event.title}`}
      </UI.H4>

      <UI.Paragraph>
        {t('add_order_description')}
      </UI.Paragraph>

      <UI.ServerErrors error={error} />

      <UI.HR />

      <UI.FormGrid>
        <UI.InputGroup>
          <UI.InputLabel>
            {t('registrations')}
          </UI.InputLabel>
          <UI.FormGrid sc={{ gutter: 0.5, mt: 2 }}>
            {form.values.registrations.map((registration, index) => (
              <RegistrationForm
                form={form}
                index={index}
                errors={errors}
                event={event}
                personalRegistrations={personalRegistrations}
                nonpersonalRegistrations={nonpersonalRegistrations}
                addRegistration={addRegistration}
                deleteRegistration={deleteRegistration}
                key={registrationKeys.current[index]}
              />
            ))}
          </UI.FormGrid>
        </UI.InputGroup>

        {standAloneProducts.length > 0 && (
          <>
            <UI.HR />

            <UI.InputGroup>
              <UI.InputLabel>
                {t('stand_alone_products')}
              </UI.InputLabel>
              <UI.FormGrid sc={{ gutter: 0.5, mt: 2 }}>
                {form.values.stand_alone_upgrades.map((upgrade, index) => (
                  <UpgradeForm
                    form={form}
                    upgradeKey={`stand_alone_upgrades.${index}`}
                    errors={errors}
                    products={standAloneProducts}
                    onChange={(data) => setStandAloneUpgrade(data, index)}
                    onDelete={() => deleteStandAloneUpgrade(index)}
                    key={`upgrade_${standAloneUpgradeKeys.current[index]}`}
                  />
                ))}
              </UI.FormGrid>
            </UI.InputGroup>
          </>
        )}

        {event.coupons.length > 0 && (
          <>
            <UI.HR />
            <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr'] }}>
              <UI.InputGroup
                sc={{
                  valid: !errors.coupon,
                  touched: form.getTouched('coupon'),
                }}
              >
                <UI.InputLabel htmlFor="Coupon">
                  {t('coupon')}
                </UI.InputLabel>
                {(!selectedCoupon || selectedCoupon.codes_count === 0) && (
                  <UI.Select
                    value={
                      selectedCoupon
                        ? event.coupons.findIndex(({ id }) => id === selectedCoupon?.id)
                        : -1
                    }
                    onChange={handleCouponChange}
                    id="Coupon"
                  >
                    <option value={-1}> </option>
                    {event.coupons.map((coupon, index) => (
                      <option value={index} key={coupon.id}>
                        {coupon.code.value}
                        {' • '}
                        {coupon.type === CouponType.absolute && formatCurrency(-coupon.value)}
                        {coupon.type === CouponType.percentage && `${formatInteger(-coupon.value)}%`}
                        {!coupon.available && ` • ${t('used_up')}`}
                      </option>
                    ))}
                  </UI.Select>
                )}

                {selectedCoupon?.codes_count > 0 && (
                  <UI.InputWrapper>
                    <UI.DebouncedInput
                      {...form.text('coupon')}
                      placeholder={`${selectedCoupon.prefix}...`}
                      id="Coupon"
                      ref={couponRef}
                      sc={{ pr: 5 }}
                    />
                    <UI.Span>
                      <UI.A onClick={() => clearCoupon()} sc={{ color: 'gray.400' }}>
                        <UI.Icon>
                          <X />
                        </UI.Icon>
                      </UI.A>
                    </UI.Span>
                  </UI.InputWrapper>
                )}

                <UI.ErrorMessages
                  attribute={t('coupon')}
                  errors={errors.coupon}
                />
              </UI.InputGroup>
              {hasTickets && event.invitations.length > 0 && (
                <UI.InputGroup sc={{ valid: !errors?.invitation, touched: form.getTouched('invitation') }}>
                  <UI.InputLabel htmlFor="Invitation">
                    {t('invitation_code')}
                  </UI.InputLabel>
                  {(!selectedInvitation || !selectedInvitation.has_unique_codes) && (
                    <UI.Select
                      value={
                        selectedInvitation
                          ? event.invitations.findIndex(({ id }) => id === selectedInvitation?.id)
                          : -1
                      }
                      onChange={handleInvitationChange}
                      id="Invitation"
                    >
                      <option value={-1}> </option>
                      {event.invitations.map((invitation, index) => (
                        <option value={index} key={invitation.id}>
                          {invitation.code.value}
                          {!invitation.available && ` • ${t('used_up')}`}
                        </option>
                      ))}
                    </UI.Select>
                  )}

                  {selectedInvitation?.has_unique_codes && (
                    <UI.InputWrapper>
                      <UI.DebouncedInput
                        {...form.text('invitation_code')}
                        placeholder={`${selectedInvitation.prefix}...`}
                        id="Invitation"
                        ref={invitationCodeRef}
                        sc={{ pr: 5 }}
                      />
                      <UI.Span>
                        <UI.A onClick={() => clearInvitation()} sc={{ color: 'gray.400' }}>
                          <UI.Icon>
                            <X />
                          </UI.Icon>
                        </UI.A>
                      </UI.Span>
                    </UI.InputWrapper>
                  )}

                  <UI.ErrorMessages attribute={t('invitation_code')} errors={errors.invitation} />
                </UI.InputGroup>
              )}
            </UI.FormGrid>
          </>
        )}

        {hasNonBusinessTickets && (
          <>
            <UI.HR />

            <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr'] }}>
              <UI.FormGrid>
                <FindOrAddTeamForm
                  form={form}
                  eventId={event.id}
                  captain={participant}
                  errors={errors}
                />
              </UI.FormGrid>
            </UI.FormGrid>
          </>
        )}

        <UI.HR />

        <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr'] }}>
          <UI.FormGrid>
            <UI.InputGroup>
              <UI.InputLabel>
                {t('invoice')}
              </UI.InputLabel>
              <UI.Checkbox
                checked={requireInvoiceDetails}
                onChange={() => setSpecifyInvoiceDetails((specify) => !specify)}
                name="invoice"
                disabled={hasBusinessTickets}
              >
                {t('specify_invoice_details')}
              </UI.Checkbox>
            </UI.InputGroup>
            {requireInvoiceDetails && (
              <InvoiceDetailsForm
                value={form.values.invoice}
                onChange={(value) => form.set((input) => ({ ...input, invoice: { ...input.invoice, ...value } }))}
                onBlur={(field) => form.setTouched(`invoice.${field}`, true)}
                touched={form.touched.invoice}
                errors={filterPrefix(errors, 'invoice')}
              />
            )}
          </UI.FormGrid>
        </UI.FormGrid>

        <UI.HR />

        <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr'] }}>
          <UI.InputGroup>
            <UI.InputLabel htmlFor="OrderDueDate">{t('due_date')}</UI.InputLabel>
            <UI.DatePicker
              id="OrderDueDate"
              selected={form.values.due_date}
              onChange={(value) => form.set('due_date', value)}
              selectsStart
              startDate={form.values.due_date}
            />
            <UI.ErrorMessages
              attribute={t('due_date')}
              errors={errors.due_date}
            />
          </UI.InputGroup>
          <UI.Div>
            {checkoutSummary.amount > 0 && form.values.due_date && (
              <PaymentReminderInfo dueDates={[form.values.due_date]} />
            )}
          </UI.Div>
        </UI.FormGrid>

        <Totals summary={checkoutSummary} loading={loadingTotals} />

        <UI.Div>
          <UI.FormButtons
            form={form}
            submitLabel={t('add_order')}
            loading={loading}
            valid={valid}
          />
        </UI.Div>
      </UI.FormGrid>
    </UI.Form>
  );
};

interface RegistrationFormProps {
  form: FormManager<AddOrderData>;
  index: number;
  errors: ErrorBag;
  event: Event;
  personalRegistrations: Registration[];
  nonpersonalRegistrations: Registration[];
  addRegistration: () => void;
  deleteRegistration: (index: number) => void;
}

const RegistrationForm = ({
  form, index, errors, event, personalRegistrations, nonpersonalRegistrations, addRegistration, deleteRegistration,
}: RegistrationFormProps) => {
  const { t } = useTranslation();
  const formatTimeSlot = useTimeSlotFormatter();
  const currencyInput = useCurrencyInput();
  const integerInput = useIntegerInput();

  const registrationKey = `registrations.${index}`;
  const registrationData = form.values.registrations[index];

  const registrations = [...personalRegistrations, ...nonpersonalRegistrations];

  const registration = registrations.filter(
    (registration) => registration.id === registrationData.registration?.id,
  )[0];

  const ticket = event.tickets.filter(
    (ticket) => registration?.ticket.id === ticket.id || ticket.promotions.filter(
      ({ id }) => id === registrationData.ticket?.promotion.id,
    ).length > 0,
  )[0];

  const promotions = ticket?.promotions || [];

  const ticketProducts = event.products.filter((product) => !product.stand_alone);
  const eventHasTicketProducts = ticketProducts.length > 0;

  // Existing registrations can only be updated when existing registrations exists,
  // and when the event is providing upgrades.
  const showRegistrations = registrations.length > 0 && eventHasTicketProducts;

  const setTicket = (id: string) => {
    const ticket = event.tickets.filter((ticket) => ticket.id === id)[0];
    const registration = registrations.filter((registration) => registration.id === id)[0];

    form.set(registrationKey, (registrationData: RegistrationData) => ({
      ...registrationData,
      registration: registration ? { id: registration.id } : undefined,
      ticket: ticket ? {
        promotion: { id: ticket.promotions[0].id },
        time_slot: ticket.time_slots?.length > 0 ? { id: ticket.time_slots[0].id } : undefined,
        amount: ticket.promotions[0].amount,
      } : undefined,
      upgrades: [
        ...registrationData.upgrades,
        ...(registrationData.upgrades.length === 0 && eventHasTicketProducts ? [emptyUpgrade] : []),
      ],
    }));

    if (index === form.values.registrations.length - 1) {
      addRegistration();
    }
  };

  const setPromotion = (id: string) => {
    const promotion = promotions.filter((promotion) => promotion.id === id)[0];

    form.set(registrationKey, (registrationData: RegistrationData) => ({
      ...registrationData,
      ticket: {
        ...registrationData.ticket,
        promotion: { id },
        amount: promotion.amount,
      },
    }));
  };

  const setTimeSlot = (id: string) => {
    form.set(registrationKey, (registrationData: RegistrationData) => ({
      ...registrationData,
      ticket: {
        ...registrationData.ticket,
        time_slot: { id },
      },
    }));
  };

  // Generate unique key props for the list of upgrade forms
  const upgradeKeys = useRef(registrationData.upgrades.map((value, index) => index));

  const setUpgrade = (upgradeData: UpgradeData, upgradeIndex: number) => {
    form.set(`${registrationKey}.upgrades.${upgradeIndex}`, upgradeData);

    if (upgradeIndex === registrationData.upgrades.length - 1) {
      addUpgrade();
    }
  };

  const addUpgrade = () => {
    form.set(registrationKey, (registrationData: RegistrationData) => ({
      ...registrationData,
      upgrades: [...registrationData.upgrades, emptyUpgrade],
    }));
    upgradeKeys.current = [...upgradeKeys.current, Math.max(0, ...upgradeKeys.current) + 1];
  };

  const deleteUpgrade = (index: number) => {
    form.delete(`${registrationKey}.upgrades`, index);
    upgradeKeys.current.splice(index, 1);
  };

  // Hide the 'Select ticket' option if this registration would exceed the maximum number of registrations per order.
  if (!ticket && form.values.registrations.length === (MAX_REGISTRATIONS + 1)) {
    return <></>;
  }

  return (
    <UI.FormGrid sc={{ gutter: 0.5 }}>
      <UI.FormGrid sc={{ columns: ['1fr', '4fr 2fr 2fr 1fr 48px'], gutter: 0.5 }}>
        <UI.FormGrid sc={{ columns: ticket ? '67px 1fr' : '1fr', gutter: 0.5 }}>
          {ticket && (
            <UI.Div>
              <UI.DebouncedInput
                {...form.text(`${registrationKey}.quantity`, {
                  inputHandler: integerInput(),
                })}
                maxLength={3}
                inputMode="numeric"
                format={integerInput().format}
                match={/[\d]/g}
                sc={{ mr: 1 }}
                style={{ width: 50, textAlign: 'center' }}
                aria-label={`${t('quantity')} (${ticket.title})`}
                disabled={!!registration}
              />
              &times;
            </UI.Div>
          )}
          <UI.Div>
            <UI.Select
              onChange={(event) => {
                setTicket(event.target.value);
                event.target.blur();
              }}
              value={registration?.id || ticket?.id || ''}
              style={{ fontWeight: registration?.id || ticket?.id ? 500 : undefined }}
            >
              {!ticket && (
                <>
                  {!showRegistrations && <option value="">{t('select_ticket')}</option>}
                  {showRegistrations && <option value="">{t('select_ticket_or_registration')}</option>}
                  {showRegistrations && <option disabled value="">&mdash;</option>}
                </>
              )}
              {showRegistrations && <option disabled value="">{t('new_registration')}</option>}
              {event.tickets.map((ticket) => (
                <option value={ticket.id} key={ticket.id}>
                  {ticket.title}
                  {ticket.is_sold_out && ` • ${t('sold_out')}`}
                </option>
              ))}
              {showRegistrations && (
                <>
                  {personalRegistrations.length > 0 && (
                    <>
                      <option disabled value="">&mdash;</option>
                      <option disabled value="">
                        {t('existing_registration', { count: personalRegistrations.length })}
                      </option>
                      {personalRegistrations.map((registration) => (
                        <option value={registration.id} key={registration.id}>
                          {registration.ticket.title}
                          {` • #${registration.sequence}`}
                        </option>
                      ))}
                    </>
                  )}
                  {nonpersonalRegistrations.length > 0 && (
                    <>
                      <option disabled value="">&mdash;</option>
                      <option disabled value="">
                        {t('other_registration', { count: nonpersonalRegistrations.length })}
                      </option>
                      {nonpersonalRegistrations.map((registration) => (
                        <option value={registration.id} key={registration.id}>
                          {registration.ticket.title}
                          {registration.assigned && ` • ${registration.full_name}`}
                          {!registration.assigned && ` • ${t('unassigned')}`}
                          {` • #${registration.sequence}`}
                        </option>
                      ))}
                    </>
                  )}
                </>
              )}
            </UI.Select>
          </UI.Div>
        </UI.FormGrid>
        {ticket && (
          <>
            <UI.Div>
              <UI.Select
                onChange={(event) => setPromotion(event.target.value)}
                value={registrationData.ticket?.promotion.id || registration?.promotion.id}
                disabled={!!registration}
              >
                {promotions.map((promotion) => (
                  <option value={promotion.id} key={promotion.id}>
                    {promotion.title || t('standard')}
                    {promotion.is_sold_out && ` • ${t('sold_out')}`}
                  </option>
                ))}
              </UI.Select>
            </UI.Div>
            <UI.Div>
              <UI.Select
                onChange={(event) => setTimeSlot(event.target.value)}
                value={registrationData.ticket?.time_slot?.id || registration?.time_slot?.id}
                disabled={ticket.time_slots.length === 0 || !!registration}
              >
                {!registrationData.ticket?.time_slot?.id && (
                  <option value="">{t('time_slot')}</option>
                )}
                {ticket.time_slots.map((timeSlot) => (
                  <option value={timeSlot.id} key={timeSlot.id}>
                    {formatTimeSlot(timeSlot)}
                    {timeSlot.title && ` • ${timeSlot.title}`}
                    {timeSlot.is_sold_out && ` • ${t('full')}`}
                  </option>
                ))}
              </UI.Select>
            </UI.Div>
            <UI.Div>
              <UI.InputGroup
                sc={{
                  valid: !errors[`${registrationKey}.ticket.amount`],
                  touched: form.getTouched(`${registrationKey}.ticket.amount`),
                }}
              >
                <UI.CurrencyInput
                  {...form.text(`${registrationKey}.ticket.amount`, {
                    inputHandler: {
                      parse: currencyInput.parse,
                      format: currencyInput.value,
                    },
                  })}
                  disabled={!!registration}
                  key={registrationData.ticket?.promotion.id}
                />
                <UI.ErrorMessages
                  attribute={t('amount')}
                  errors={errors[`${registrationKey}.ticket.amount`]}
                />
              </UI.InputGroup>
            </UI.Div>
            <UI.Div sc={{ textAlign: 'right' }}>
              <UI.Button
                onClick={() => deleteRegistration(index)}
                sc={{ blank: true, color: 'gray.400' }}
              >
                <UI.Icon>
                  <Trash2 />
                </UI.Icon>
              </UI.Button>
            </UI.Div>
          </>
        )}
      </UI.FormGrid>
      {registrationData.upgrades.map((upgrade, upgradeIndex) => (
        <UpgradeForm
          form={form}
          upgradeKey={`${registrationKey}.upgrades.${upgradeIndex}`}
          errors={errors}
          ticket={ticket}
          products={ticketProducts}
          onChange={(data) => setUpgrade(data, upgradeIndex)}
          onDelete={() => deleteUpgrade(upgradeIndex)}
          key={`upgrade_${upgradeKeys.current[upgradeIndex]}`}
        />
      ))}
    </UI.FormGrid>
  );
};

interface UpgradeFormProps {
  form: FormManager<AddOrderData>;
  upgradeKey: string;
  errors: ErrorBag;
  ticket?: Ticket;
  products: Product[];
  onChange: (data: UpgradeData) => void;
  onDelete: () => void;
}

const UpgradeForm = ({
  form, upgradeKey, errors, ticket, products, onChange, onDelete,
}: UpgradeFormProps) => {
  const { t } = useTranslation();
  const currencyInput = useCurrencyInput();
  const integerInput = useIntegerInput();

  const upgradeData = form.get(upgradeKey);

  const product = products.filter(
    (product) => product.promotions.filter(({ id }) => id === upgradeData.promotion?.id).length > 0,
  )[0];

  const promotions = product?.promotions || [];

  const setProduct = (id: string) => {
    const product = products.filter((product) => product.id === id)[0];

    onChange({
      ...upgradeData,
      promotion: { id: product.promotions[0].id },
      // Product variant is not required for business tickets.
      product_variant: ticket?.business ? undefined : (product.product_variants?.length > 0 ? {
        id: product.product_variants[0].id,
      } : undefined),
      amount: product.promotions[0].amount,
    });
  };

  const setPromotion = (id: string) => {
    const promotion = promotions.filter((promotion) => promotion.id === id)[0];

    form.set(upgradeKey, (upgradeData: UpgradeData) => ({
      ...upgradeData,
      promotion: { id },
      amount: promotion.amount,
    }));
  };

  const setProductVariant = (id: string) => {
    form.set(upgradeKey, (upgradeData: UpgradeData) => ({
      ...upgradeData,
      product_variant: id ? { id } : undefined,
    }));
  };

  const isTicketUpgrade = upgradeKey.startsWith('registrations');

  return (
    <UI.FormGrid sc={{ columns: ['1fr', '4fr 2fr 2fr 1fr 48px'], gutter: 0.5 }}>
      <UI.FormGrid sc={{ columns: `${isTicketUpgrade ? '67px' : ''} ${product ? '67px' : ''} 1fr`, gutter: 0.5 }}>
        {isTicketUpgrade && <UI.Div />}
        {product && (
          <UI.Div>
            <UI.DebouncedInput
              {...form.text(`${upgradeKey}.quantity`, {
                inputHandler: integerInput(),
              })}
              maxLength={3}
              inputMode="numeric"
              format={integerInput().format}
              match={/[\d]/g}
              sc={{ mr: 1 }}
              style={{ width: 50, textAlign: 'center' }}
              aria-label={`${t('quantity')} (${product.title})`}
            />
            &times;
          </UI.Div>
        )}
        <UI.Div>
          <UI.Select
            onChange={(event) => { setProduct(event.target.value); event.target.blur(); }}
            value={product?.id || ''}
          >
            {!upgradeData.promotion?.id && <option value="">{t('add_an_extra')}</option>}
            {products.map((product) => (
              <option value={product.id} key={product.id}>
                {product.title}
                {product.is_sold_out && ` • ${t('sold_out')}`}
              </option>
            ))}
          </UI.Select>
        </UI.Div>
      </UI.FormGrid>
      {product && (
        <>
          <UI.Div>
            <UI.Select
              onChange={(event) => setPromotion(event.target.value)}
              value={upgradeData.promotion?.id}
              disabled={!upgradeData.promotion?.id}
            >
              {!upgradeData.promotion?.id && <option value="">{t('promotion')}</option>}
              {promotions.map((promotion) => (
                <option value={promotion.id} key={promotion.id}>
                  {promotion.title || t('standard')}
                  {promotion.is_sold_out && ` • ${t('sold_out')}`}
                </option>
              ))}
            </UI.Select>
          </UI.Div>
          <UI.Div>
            <UI.Select
              value={upgradeData.product_variant?.id}
              onChange={(event) => setProductVariant(event.target.value)}
              disabled={product.product_variants.length === 0}
            >
              <option value="">{t('product_variant')}</option>
              {product?.product_variants.map((productVariant) => (
                <option
                  value={productVariant.id}
                  key={productVariant.id}
                >
                  {productVariant.title}
                  {productVariant.is_sold_out && ` • ${t('sold_out')}`}
                </option>
              ))}
            </UI.Select>
          </UI.Div>
          <UI.Div>
            <UI.InputGroup
              sc={{
                valid: !errors[`${upgradeKey}.amount`], touched: form.getTouched(`${upgradeKey}.amount`),
              }}
            >
              <UI.CurrencyInput
                {...form.text(`${upgradeKey}.amount`, {
                  inputHandler: {
                    parse: currencyInput.parse,
                    format: currencyInput.value,
                  },
                })}
                disabled={!upgradeData.promotion?.id}
                key={upgradeData.promotion?.id}
              />
              <UI.ErrorMessages
                attribute={t('amount')}
                errors={errors[`${upgradeKey}.amount`]}
              />
            </UI.InputGroup>
          </UI.Div>
          <UI.Div sc={{ textAlign: 'right' }}>
            <UI.Button
              onClick={() => onDelete()}
              sc={{ blank: true, color: 'gray.400' }}
            >
              <UI.Icon>
                <Trash2 />
              </UI.Icon>
            </UI.Button>
          </UI.Div>
        </>
      )}
    </UI.FormGrid>
  );
};

interface AddOrderModalProps {
  participantId?: string;
  eventId?: string;
  onSuccess?: (order: AddedOrder) => void;
  onClose?: () => void;
  refetchQueries?: DocumentNode[];
}

const useAddOrderModal = ({
  participantId,
  eventId,
  onSuccess,
  onClose,
  refetchQueries,
}: AddOrderModalProps) => {
  const handleSuccess = (order: AddedOrder) => {
    onSuccess?.(order);
    closeModal();
  };

  const handleClose = () => {
    onClose?.();
    closeModal();
  };

  const [openModal, closeModal] = useModal(() => (
    <AddOrderModal
      participantId={participantId}
      eventId={eventId}
      onSuccess={handleSuccess}
      onClose={handleClose}
      refetchQueries={refetchQueries}
    />
  ), [participantId, eventId, onSuccess, onClose, refetchQueries]);

  return [openModal, closeModal];
};

const AddOrderModal = ({
  participantId,
  eventId,
  onSuccess,
  onClose,
  refetchQueries = [GetParticipantOrders],
}: AddOrderModalProps) => {
  const [width, setWidth] = useState(600);

  return (
    <UI.Modal maxWidth={width} close={onClose}>
      <AddOrderForm
        participantId={participantId}
        eventId={eventId}
        onSuccess={onSuccess}
        onContinue={() => setWidth(1000)}
        refetchQueries={refetchQueries}
      />
    </UI.Modal>
  );
};

interface TotalsProps {
  summary: CheckoutSummary;
  loading: boolean;
}

const Totals = ({ summary, loading }: TotalsProps) => {
  const { t } = useTranslation();
  const { formatCurrency } = useLocale();

  const passedOnFee = summary?.passed_on_fee || 0;
  const passedOnFeeVatAmount = summary?.passed_on_fee_vat_amount || 0;
  const amount = summary?.amount || 0;
  const vatAmount = summary?.vat_amount || 0;

  return (
    <UI.Info sc={{ textAlign: 'right', muted: loading }}>
      {passedOnFee > 0 && (
        <UI.Div sc={{ mb: 2 }}>
          {`${t('platform_fee')}: ${formatCurrency(passedOnFee)}`}
        </UI.Div>
      )}
      <UI.Div sc={{ strong: true }}>
        {`${t('total')}: ${formatCurrency(amount + passedOnFee)}`}
      </UI.Div>
      {(vatAmount + passedOnFeeVatAmount) > 0 && (
        <UI.Div sc={{ mt: 2 }}>
          {`${t('included_taxes')}: ${formatCurrency(vatAmount + passedOnFeeVatAmount)}`}
        </UI.Div>
      )}
    </UI.Info>
  );
};

export default useAddOrderModal;
